import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { IRootState } from 'src/reducers';
import PersonnelSearch from '../components/PersonnelSearch';
import useFetch from 'use-http';
import BillingInfo from 'src/models/personnel/BillingInfo';
import PersonnelBillingInfoForm from 'src/models/personnel/PersonnelBillingInfoForm';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import DefaultBill from 'src/models/personnel/DefaultBill';
import DialogList from 'src/dialogs';
import actions from 'src/actions';
import DefaultBillHistoryDialog from '../dialogs/DefaultBillHistoryDialog';
import PersonnelNavBar from '../components/PersonnelNavBar';

const BillingPage = () => {
    const [filteredBilling, setFilteredBilling] = useState<BillingInfo[]>([]);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [personnelBilling, setPersonnelBilling] = useState<Array<BillingInfo>>(undefined); // The whole personnel billing list
    const [defaultBillHistory, setDefaultBillHistory] = useState<Array<DefaultBill>>(undefined); // The whole default pay history list
    const [currentDefaultBill, setCurrentDefaultBill] = useState<DefaultBill>(undefined);
    const { register, errors, handleSubmit, setValue, getValues, reset } = useForm();
    const { get, put, post, response } = useFetch();

    const dispatch = useDispatch();
    const appContext = useContext<IAppContextState>(AppContext);
    const selectedPersonnel = useSelector((state: IRootState) => state.landman.selectedPersonnel);

    // Initial render
    useEffect(() => {
        onCreate();
        
        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        if (defaultBillHistory?.length > 0) {
            console.log('====> defaultBillHistory = ', defaultBillHistory);
            let currentDF = defaultBillHistory?.find(df => df.isActive === true);

            if (currentDF) {
                setCurrentDefaultBill(currentDF);
            }
        } else {
            setCurrentDefaultBill(new DefaultBill());
        }
    }, [defaultBillHistory]);

    useEffect(() => {
        if (!!currentDefaultBill) {
            setValue('currentDefaultBill.defaultBillRate', currentDefaultBill?.defaultBillRate ? currentDefaultBill?.defaultBillRate : ''); 
            setValue('currentDefaultBill.defaultBillUnit', currentDefaultBill?.defaultBillUnit ? currentDefaultBill?.defaultBillUnit : '');
        }
    }, [currentDefaultBill]);

    useEffect(() => {
        initializeData();
    }, [selectedPersonnel]);

    useEffect(() => {
        if (!!personnelBilling) {
            console.log('====> Personnel Billing for Selected Personnel', personnelBilling);
            setFilteredBilling(sortByActive(personnelBilling));
        }
    }, [personnelBilling]);

    const onCreate = () => {
        console.log('BillingPage onCreate()');
        initializeData();
    };

    const onDestroy = () => {
        console.log('BillingPage onDestroy()');
    };

    const getPersonnelBilling = async () => {
        setIsLoading(true);
        let data = await get(`v1.0/landmen/${selectedPersonnel?.id}/get-personnel-billing`);

        if (response.ok) {
            setPersonnelBilling(data);
        } else {
            window.alert('Unable to Retrieve Selected Personnel\'s Billing Information.');
        }
        setIsLoading(false);
    };

    const getDefaultBillHistoryByUserId = async () => {
        setIsLoading(true);
        let data = await get(`v1.0/landmen/${selectedPersonnel?.id}/get-defaultBill-history`);

        if (response.ok) {
            setDefaultBillHistory(data);
        } else {
            window.alert('Unable to Retrieve Selected Personnel\'s Billing Information.');
        }
        setIsLoading(false);
    };

    const updatePersonnelBilling = async (data: PersonnelBillingInfoForm) => {
        if (!!data.filteredBilling.length) {
            setIsLoading(true);
            await put(`v1.0/landmen/${selectedPersonnel?.id}/save-personnel-billing`, data.filteredBilling);

            if (response.ok) {
                window.alert('Update Successful');
                reset(data);
            } else {
                window.alert(`Unable to Update Selected Personnel\'s Billing Information.`);
            }
            setIsLoading(false);
        }
    };

    const displayTitle = () => {
        const title = selectedPersonnel ? `Billing Exceptions - ${selectedPersonnel?.firstName} ${selectedPersonnel?.lastName}` : 'Billing Exceptions' ;

        return title;
    };

    const showContractPayHistory = async () => {
        if (!!selectedPersonnel) {
            await getDefaultBillHistoryByUserId();
            dispatch(actions.layout.showDialog(DialogList.DEFAULT_BILL_HISTORY));
        } else if (!selectedPersonnel) {
            window.alert('No Personnel Selected');
        }
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };

    const filter = () => {
        let values = getValues();
        let filteredItems = personnelBilling;
        console.log(values);

        if (values?.display === 'all') {
            filteredItems = sortByActive(filteredItems);
        } else if (values?.display === 'active') {
            filteredItems = filteredItems?.filter(c => c.isActive);
        } else if (values?.display === 'inactive') {
            filteredItems = filteredItems?.filter(c => !c.isActive);
        }
    
        setFilteredBilling(filteredItems);
    };

    const sortByActive = (data: BillingInfo[]) => {
        data.sort((a, b) => {
            if (a.isActive) {
                return -1;
            } else {
                return 1;
            }
        });

        return data;
    };

    const initializeData = () => {
        if (!!selectedPersonnel && selectedPersonnel?.id) {
            getPersonnelBilling();
            getDefaultBillHistoryByUserId();
        }
    };

    const finalizeData = (data: PersonnelBillingInfoForm) => {
        let finalData: PersonnelBillingInfoForm = {
            ...data,
            filteredBilling: data.filteredBilling,
            currentDefaultBill: {
                id: currentDefaultBill?.id,
                createdBy: appContext.currentUser.username,
                userId: selectedPersonnel?.id,
                defaultBillRate: parseInt(data?.currentDefaultBill?.defaultBillRate + '', 10),
                defaultBillUnit: data?.currentDefaultBill?.defaultBillUnit,
                isActive: currentDefaultBill?.isActive,
                createdDate: currentDefaultBill?.createdDate
            }
        };

        // convert billRates to numbers instead of strings
        // set the ids of the billingInfo to update the db
        for (let i = 0; i < finalData.filteredBilling?.length; i++) {
            finalData.filteredBilling[i].payRate = personnelBilling[i].payRate;
            finalData.filteredBilling[i].billRate = parseFloat(finalData.filteredBilling[i].billRate + '');
            finalData.filteredBilling[i].classification = personnelBilling[i].classification;
            finalData.filteredBilling[i].id = filteredBilling[i].id;
        }

        return finalData;
    };

    const onSubmit = async (data: PersonnelBillingInfoForm) => {
        if (!!selectedPersonnel) {
            let finalData = finalizeData(data);
            updatePersonnelBilling(finalData);
            createDefaultBillRate(finalData);
        } else {
            window.alert('No Personnel Selected');
        }
    };

    const createDefaultBillRate = async (data: PersonnelBillingInfoForm) => {
        if (selectedPersonnel && data.currentDefaultBill?.defaultBillRate && data.currentDefaultBill?.defaultBillUnit) {
            let result = await post(`/v1.0/landmen/${selectedPersonnel?.id}/create-personnel-defaultBill`, data.currentDefaultBill);

            if (response.ok) {
                // getDefaultPayHistoryByUserId();
                window.alert('Update successful');
            } else if (result?.messageCode === '15') {
                window.alert('Update failed');
            } else {
                window.alert('Update failed');
            }
        }
    };

    const showPersonnelData = () => {
        console.log('Display the Stuffs');
    };

    const displayBillingInfo = () => {
        return <form id="billingForm" onSubmit={handleSubmit(onSubmit)}>
        <div>
            <table>
                <thead>
                    <tr>
                        <th/>
                        <th>Client</th>
                        <th>Client Name</th>
                        <th>Project</th>
                        <th>Project Name</th>
                        <th>Pay By</th>
                        <th>Default Day Rate</th>
                        <th/>
                    </tr>
                </thead>
                {filteredBilling?.map((bill: BillingInfo, index: number) => {
                     return (
                        <tbody key={bill.id}>
                            <tr className="table-item-hover" style={ bill.isActive ? {padding: '1.5rem', textAlign: 'center', background: '#ffff7d'} : {textAlign: 'center'}}>
                                <td style={{width: '4%'}}>
                                    <RHFCheckbox
                                        name={`filteredBilling[${index}].isSelected`}
                                        register={register}
                                        errors={errors}
                                    />
                                </td>
                                <td style={{width: '4%'}}>
                                    {bill.clientId}
                                </td>
                                <td style={{width: '20%'}}>
                                    {bill.clientName}
                                </td>
                                <td style={{width: '5%'}}>
                                    {bill.projectId}
                                </td>
                                <td style={{width: '18%'}}>
                                    {bill.projectName} 
                                </td>
                                <td style={{width: '10%'}}>
                                    <RHFSelect 
                                        name={`filteredBilling[${index}].payUnit`}
                                        defaultVal={bill.payUnit}
                                        options={[
                                            { value: 'Day', label: 'Day' },
                                            { value: 'Hour', label: 'Hour' },
                                        ]}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                    />
                                </td>
                                <td style={{width: '10%'}}>
                                    <RHFInputBox 
                                        name={`filteredBilling[${index}].billRate`}
                                        defaultVal={`${bill.billRate}`}
                                        type="number"
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <Button 
                                        className="blue-hover-button" 
                                        style={{width: '100%', height: '3rem'}}
                                        text={'History'} 
                                        onClick={() => showContractPayHistory()}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    );
                })}
            </table>
        </div>
        </form>;
    };

    return (
        <div>
            <PersonnelNavBar/>
            <PersonnelSearch showPersonnelData={showPersonnelData} setIsCollapsed={setIsCollapsed}/>
            <Page>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3"> 
                        <Panel>
                            <div className="form-row" style={{textAlign: 'center', verticalAlign: 'middle', margin: '0'}}>
                                <RHFSelect 
                                    name={`currentDefaultBill.defaultBillUnit`}
                                    placeholder="Default Bill Unit"
                                    options={[
                                        { value: 'Day', label: 'Day' },
                                        { value: 'Hour', label: 'Hour' },
                                        { value: 'Month', label: 'Month' },
                                    ]}
                                    style={{width: '100%', border: '.5px solid LightGray', borderRadius: '5px'}}
                                    onChange={() => filter()}
                                    bodyStyle={{width: '18%'}}
                                    register={register} 
                                    errors={errors}
                                    title="Default Bill Unit"
                                />
                                <RHFInputBox 
                                    name={`currentDefaultBill.defaultBillRate`}
                                    placeholder="Default Bill Rate"
                                    type="number"
                                    style={{width: '100%', border: '.5px solid LightGray', borderRadius: '5px'}}
                                    onEnterPressed={(e) => e.key === 'Enter' ? filter() : {}}
                                    bodyStyle={{width: '18%', marginLeft: '2rem'}}
                                    register={register} 
                                    errors={errors}
                                    title="Default Bill Rate"
                                />
                                <Button 
                                    className="blue-hover-button" 
                                    style={{width: '19%', height: '3rem', marginLeft: '2rem', borderRadius: '5px'}}
                                    text={'Update'} 
                                    type="submit"
                                    form="billingForm"
                                />
                                <Button 
                                    className="blue-hover-button" 
                                    style={{width: '19%', height: '3rem', marginLeft: '3rem', borderRadius: '5px'}}
                                    text={'History'} 
                                    onClick={() => showContractPayHistory()}
                                />
                                <RHFSelect 
                                    name={`display`}
                                    placeholder="Display Prospect Staffing"
                                    options={[
                                        { value: 'all', label: 'All' },
                                        { value: 'active', label: 'Active' },
                                        { value: 'inactive', label: 'Inactive' },
                                    ]}
                                    style={{width: '100%', border: '.5px solid LightGray', borderRadius: '5px'}}
                                    onChange={() => filter()}
                                    bodyStyle={{width: '18%', marginLeft: '2rem'}}
                                    register={register} 
                                    errors={errors}
                                />
                            </div>
                        </Panel>
                    </Panel>
                    <div style={{height: '.1rem'}}/>
                    <Panel backgroundColor="#D3D3D3"> 
                        <Panel panelTitle={displayTitle()}>
                            { filteredBilling?.length && (
                                <div className={isCollapsed ? 'medium-panel' : 'medium-panel-with-search'} style={{overflowY: 'scroll', padding: '2rem', paddingTop: '0'}}>
                                    {displayBillingInfo()}
                                </div>)}
                                { isLoading && <LoadingSpinner/>}
                                { !isLoading && !filteredBilling?.length && (
                                    <div style={{textAlign: 'center', fontWeight: 'bold', fontSize: '20px', marginBottom: '3rem'}}>
                                        No Billing Info
                                    </div>)}
                            </Panel>
                        </Panel> 
                    </Page.Section>
                </Page>
            <DefaultBillHistoryDialog 
                dialogName={DialogList.DEFAULT_BILL_HISTORY} 
                hideDialog={hideDialog}
                defaultBillHistory={defaultBillHistory}
            />
        </div>
    );
};

export default BillingPage;


// WEBPACK FOOTER //
// ./src/components/personnel/pages/BillingPage.tsx